import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Eye, Handshake } from "lucide-react";
import { ethos } from "../data/mindscope";
import { Reveal, RevealStagger, StaggerItem } from "../components/mindscope/Reveal";

const puzzlePieces = [
  { letters: "ST", col: "col-start-1 row-start-1", delay: 0 },
  { letters: "RA", col: "col-start-2 row-start-1", delay: 0.08 },
  { letters: "TE", col: "col-start-3 row-start-1", delay: 0.16 },
  { letters: "GY", col: "col-start-4 row-start-1", delay: 0.24 },
];

export default function Vision() {
  return (
    <main data-testid="vision-page" className="bg-ms-navy text-white overflow-hidden">
      {/* Header */}
      <section className="pt-40 pb-16 bg-ms-navy grain relative">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <Reveal>
            <div className="eyebrow mb-6">Chapter Two & Three</div>
            <h1 className="font-display text-6xl md:text-8xl lg:text-[10rem] leading-[0.9] tracking-[-0.03em]">
              Strategic <span className="italic text-ms-gold">Intent</span>
            </h1>
          </Reveal>
        </div>
      </section>

      {/* Puzzle STRATEGY */}
      <section className="section-y bg-ms-navy relative">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-16 items-center">
          <div className="lg:col-span-6">
            <Reveal>
              <div className="eyebrow mb-6">The Framework</div>
              <p className="font-display italic text-2xl md:text-3xl text-white/70 leading-snug mb-8">
                Strategy is not a word. It is a puzzle —
              </p>
              <p className="font-body text-white/80 text-lg leading-relaxed">
                assembled slowly, in the private hours before a board meeting, when the founder is
                honest, the numbers are naked, and the capital table is finally drawn as it truly
                stands. Our job is to hold that pen.
              </p>
            </Reveal>
          </div>

          <div className="lg:col-span-6">
            <div className="grid grid-cols-4 grid-rows-1 gap-2 aspect-[4/1.2]">
              {puzzlePieces.map((p) => (
                <motion.div
                  key={p.letters}
                  initial={{ opacity: 0, y: 40, rotate: -6 }}
                  whileInView={{ opacity: 1, y: 0, rotate: 0 }}
                  viewport={{ once: true, margin: "-80px" }}
                  transition={{ duration: 0.9, delay: p.delay, ease: [0.16, 1, 0.3, 1] }}
                  className={`${p.col} flex items-center justify-center bg-white/5 border border-ms-gold/30 rounded-sm relative overflow-hidden group hover:bg-ms-gold hover:text-ms-navy transition-colors duration-500`}
                >
                  <span className="font-display text-6xl md:text-8xl text-white group-hover:text-ms-navy transition-colors duration-500">
                    {p.letters}
                  </span>
                  <span className="absolute top-2 left-2 text-[0.6rem] tracking-widest text-ms-gold/70 group-hover:text-ms-navy/70">
                    0{puzzlePieces.indexOf(p) + 1}
                  </span>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Vision & Mission */}
      <section className="section-y bg-[#050914] relative">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid md:grid-cols-2 gap-px bg-white/5">
          <Reveal className="bg-[#050914] p-10 md:p-14">
            <div className="flex items-center gap-4 mb-8 text-ms-gold">
              <Eye size={32} strokeWidth={1.2} />
              <span className="font-body text-xs tracking-[0.32em] uppercase">Vision</span>
            </div>
            <h3 className="font-display text-4xl md:text-5xl text-white leading-[0.98] mb-6">
              To be the <span className="italic text-ms-gold">standard-bearer</span> of principled capital advisory in India.
            </h3>
            <p className="font-body text-white/70 leading-relaxed">
              A house that founders return to across three enterprises and two generations — because
              our discretion, cadence and outcome-fidelity earn a place at the family table, not just
              the boardroom.
            </p>
          </Reveal>
          <Reveal delay={0.15} className="bg-[#050914] p-10 md:p-14">
            <div className="flex items-center gap-4 mb-8 text-ms-gold">
              <Handshake size={32} strokeWidth={1.2} />
              <span className="font-body text-xs tracking-[0.32em] uppercase">Mission</span>
            </div>
            <h3 className="font-display text-4xl md:text-5xl text-white leading-[0.98] mb-6">
              To unlock capital <span className="italic text-ms-gold">without compromise</span>.
            </h3>
            <p className="font-body text-white/70 leading-relaxed">
              We commit to translating regulatory complexity into board-ready clarity — enabling
              first-time issuers and seasoned promoters to enter, remain in, or exit public markets
              with grace, rigor and durable value.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Ethos Grid */}
      <section className="section-y bg-ms-navy relative">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="grid lg:grid-cols-12 gap-10 mb-16 items-end">
            <Reveal className="lg:col-span-7">
              <div className="eyebrow mb-6">04 — The Mindscope Ethos</div>
              <h2 className="font-display text-5xl md:text-6xl leading-[0.95] text-white">
                Six principles. <span className="italic text-ms-gold">Non-negotiable.</span>
              </h2>
            </Reveal>
            <Reveal className="lg:col-span-4 lg:col-start-9" delay={0.15}>
              <p className="font-body text-white/70 leading-relaxed">
                Our ethos is not a wall-poster. It is the private argument we have with ourselves
                before every recommendation.
              </p>
            </Reveal>
          </div>

          <RevealStagger className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10">
            {ethos.map((e) => (
              <StaggerItem key={e.num}>
                <div className="bg-ms-navy p-8 md:p-10 h-full gold-border-hover !border-0 relative group">
                  <div className="flex items-baseline justify-between mb-6">
                    <span className="font-display text-5xl text-ms-gold">{e.num}</span>
                    <span className="h-px w-16 bg-ms-gold/40 group-hover:w-24 transition-[width] duration-500" />
                  </div>
                  <h3 className="font-display text-2xl md:text-3xl text-white mb-3 leading-tight">
                    {e.title}
                  </h3>
                  <p className="font-body text-white/70 leading-relaxed">{e.body}</p>
                </div>
              </StaggerItem>
            ))}
          </RevealStagger>
        </div>
      </section>

      {/* CTA */}
      <section className="section-y bg-ms-cream">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 flex flex-col lg:flex-row items-start lg:items-end justify-between gap-8">
          <Reveal>
            <div className="eyebrow mb-4" style={{ color: "#0A1128" }}><span style={{ color: "#0A1128" }}>Continue</span></div>
            <h3 className="font-display text-4xl md:text-6xl leading-[0.95] text-ms-navy">
              Chapter Four — <span className="italic">The People</span>
            </h3>
          </Reveal>
          <Link to="/leadership" data-testid="vision-next-chapter" className="btn-gold">
            Meet the Firm <ArrowRight size={16} />
          </Link>
        </div>
      </section>
    </main>
  );
}
